/* eslint-disable no-console */
import React, { useCallback, useState } from 'react';
import { Container, Link, Button, Grid, Message, Text } from 'basis';
import { useHistory } from 'react-router-dom';
import { useEventTracking } from 'react-event-tracker';
import { useTheme as emotionUseTheme } from '@emotion/react';
import {
  useStoreValue,
  OKTA_VERIFICATION_LOCKED_ERROR,
  OKTA_VERIFICATION_ERROR,
  OKTA_EMPTY_MOBILE_NUMBER_ERROR,
  OKTA_SMS_FACTOR,
  OKTA_EMAIL_FACTOR,
} from 'store';
import { LayoutPage, LayoutContent, Form, Errors, SecureIcon, OneTimeCode, Nudge } from 'components';
import { TextButton } from 'components/button';
import { byCountry, getDataLayerElements, getNextStep, submitHandler, isAU, isNZ } from 'utils';
import {
  useErrorTrackingAndUpdateStore,
  useErrorTracking,
  useFormInitialValues,
  useOktaClient,
  useSteps,
  useResume,
  useSendWelcomeEmail,
  useSaveDraft,
} from 'hooks';
import { DEV_FEATURES } from '__dev/devFeatures';
import { RESEND_MIN } from '_config/_constants/verificationPage';
import { OktaProfileError } from 'components/errors/OktaProfileError';
import styled from '@emotion/styled';
import { isFeatureOn } from 'featureToggles';
import { GuideCodeNotReceived } from 'components/errors/GuideCodeNotReceived';
import { STEP_EMAIL_VERIFICATION } from '_config';
import { useFocusOnFirstFormElement } from '../../hooks/useFocusOnFirstFormElement';

const FORM_ID = 'verification';
const EMAIL_VERIFICATION_FORM_ID = 'emailVerification';

export function Verification() {
  const [storeState, updateStore] = useStoreValue();
  const factor = storeState.oktaFactor || OKTA_SMS_FACTOR;
  const [showRedMsg, setShowRedMsg] = useState(false);
  const [resending, setResending] = useState(false);

  const theme = emotionUseTheme();
  const { pathname } = useSteps();
  const history = useHistory();
  const { trackEvent } = useEventTracking();
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore();
  const { handleErrorTracking } = useErrorTracking();
  const { resume, loading: resuming } = useResume({ applicationId: storeState.applicationId, updateStore });
  const initialValues = useFormInitialValues(factor === OKTA_SMS_FACTOR ? FORM_ID : EMAIL_VERIFICATION_FORM_ID);
  const { sendEmail: sendWelcomeEmail } = useSendWelcomeEmail();
  const { saveDraft, savingDraft } = useSaveDraft();
  const { activeStep } = useSteps();
  useFocusOnFirstFormElement();

  const noProgressStepper = getNoProgressStepper();

  function getNoProgressStepper() {
    return storeState.isResuming || storeState.isResumed || activeStep === STEP_EMAIL_VERIFICATION;
  }
  const onStartAgainClick = label => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-navigation',
        location: pathname.slice(1),
        label,
      },
    });

    updateStore({
      isResuming: false,
      verificationLocked: true,
      applicationErrors: null,
    });

    history.push('/');
  };

  const onClickRequestNewCode = () => {
    setResending(true);
    setShowRedMsg(false);
  };

  const onResendCodeClick = () => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-navigation',
        location: pathname.slice(1),
        label: 'Resend code',
      },
    });
    setShowRedMsg(false);
  };

  const onResendCodeError = error => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-navigation',
        location: pathname.slice(1),
        label: 'Resend code',
      },
    });
  };

  const handleVerifySmsOtpSuccess = useCallback(
    async ({ isLatitudeIdPasswordSet, isEmailVerified }) => {
      updateStore({
        isLatitudeIdPasswordSet,
        isEmailVerified,
        didApplicationMobileVerification: true,
      });

      if (storeState.isResuming) {
        await resume({ storeState: { ...storeState, isLatitudeIdPasswordSet, isEmailVerified } });
      } else {
        const nextStep = getNextStep({
          isLatitudeIdPasswordSet,
          factor,
          oktaProfileHealthy: storeState.oktaProfileHealthy,
          applicationSubmitted: storeState.applicationSubmitted,
        });

        if (isFeatureOn('saveAndResume')) {
          if (isAU() || isNZ()) {
            sendWelcomeEmail();
          }
          if (!savingDraft) {
            saveDraft({
              ...storeState,
              activeStep: nextStep,
            });
          }
        }

        updateStore({
          activeStep: nextStep,
          applicationErrors: null,
        });
        history.push(nextStep);
      }
    },
    [factor, history, resume, saveDraft, savingDraft, sendWelcomeEmail, storeState, updateStore],
  );

  const handleVerifyEmailOtpSuccess = useCallback(
    async ({ isLatitudeIdPasswordSet }) => {
      updateStore({
        isEmailVerified: true,
      });

      trackEvent({
        event: {
          category: 'application',
          action: 'application-navigation',
          location: 'verify-email',
          label: 'Verify',
        },
        ...getDataLayerElements(storeState),
      });

      if (isFeatureOn('emailVerification') && storeState.isResuming) {
        await resume({ storeState: { ...storeState, isLatitudeIdPasswordSet, isEmailVerified: true } });
      } else if (
        isFeatureOn('emailVerification') ||
        (!isFeatureOn('emailVerification') && !storeState.oktaProfileHealthy)
      ) {
        const nextStep = getNextStep({
          isEmailVerified: true,
          isLatitudeIdPasswordSet,
          factor,
          oktaProfileHealthy: storeState.oktaProfileHealthy,
          applicationSubmitted: storeState.applicationSubmitted,
        });

        if (isFeatureOn('saveAndResume')) {
          if (isAU()) {
            sendWelcomeEmail();
          }
          if (!savingDraft) {
            saveDraft({
              ...storeState,
              activeStep: nextStep,
            });
          }
        }

        updateStore({
          activeStep: nextStep,
          applicationErrors: null,
        });
        history.push(nextStep);
      }
    },
    [factor, history, resume, saveDraft, savingDraft, sendWelcomeEmail, storeState, trackEvent, updateStore],
  );

  const handleSuccess = useCallback(
    async ({ isLatitudeIdPasswordSet, isEmailVerified }) => {
      switch (factor) {
        case OKTA_EMAIL_FACTOR:
          return handleVerifyEmailOtpSuccess({ isLatitudeIdPasswordSet });
        case OKTA_SMS_FACTOR:
          return handleVerifySmsOtpSuccess({ isLatitudeIdPasswordSet, isEmailVerified });
        default:
          throw new Error(`Unknown factor: ${factor}`);
      }
    },
    [factor, handleVerifyEmailOtpSuccess, handleVerifySmsOtpSuccess],
  );

  const handleVerifyOTPError = error => {
    if (error.type === OKTA_VERIFICATION_LOCKED_ERROR) {
      handleErrorTracking(error);
      updateStore({
        verificationLocked: true,
      });
    } else {
      handleErrorCallback(error);
    }
  };

  const { loading: loadingOkta, verifyOTP, applySyntheticToken } = useOktaClient({
    onSuccess: handleSuccess,
    onError: handleVerifyOTPError,
  });

  const doSubmit = useCallback(
    async (verification, setErrors) => {
      trackEvent({
        event: {
          category: 'application',
          action: 'application-navigation',
          location: storeState.isResuming ? 'verify-to-resume' : 'verify',
          label: 'Verify',
        },
        ...getDataLayerElements(storeState),
      });

      if (DEV_FEATURES.OTP_SKIP) {
        const success = await applySyntheticToken();
        const isRunningSyntheticTest = !!import.meta.env.VITE_OKTA_TOKEN;
        success &&
          handleSuccess({
            isLatitudeIdPasswordSet: isRunningSyntheticTest,
            ...(factor === OKTA_EMAIL_FACTOR ? { isEmailVerified: true } : null),
          });
      } else {
        const { errorMessage } = await verifyOTP(
          verification.verificationCode,
          {
            checkIsLatitudeIdPasswordSet: true,
          },
          factor,
        );
        if (errorMessage && setErrors) {
          return setErrors({ verificationCode: errorMessage });
        }
      }
      return null;
    },
    [applySyntheticToken, factor, handleSuccess, storeState, trackEvent, verifyOTP],
  );

  const onSubmit = ({ values, errors, setErrors }) => {
    async function verifyOTPAndRedirect() {
      doSubmit(values, setErrors);
    }

    submitHandler({ submit: verifyOTPAndRedirect, errors });
  };

  const retrySubmit = useCallback(async () => {
    doSubmit(storeState.acquisition.verification);
  }, [doSubmit, storeState.acquisition.verification]);

  if ((isAU() || isNZ()) && storeState.applicationErrors?.type === OKTA_VERIFICATION_ERROR) {
    // i.e. AU customer lost network
    return (
      <LayoutPage noProgressStepper={noProgressStepper}>
        <Errors applicationRef={storeState.applicationRef} retry={retrySubmit} retrying={loadingOkta} isPreSubmission />
      </LayoutPage>
    );
  }

  if (storeState.verificationLocked) {
    return (
      <LayoutPage noProgressStepper={noProgressStepper} headerOverride="Unable to verify your identity">
        <Container margin="0 0 13 0" hasBreakpointWidth width="100%">
          <LayoutContent rowsGap="0">
            <Container>
              <Text>
                <strong>You have exceeded the maximum attempts allowed.</strong>
              </Text>
            </Container>
            <Container>
              <Text>As a result, we're unable to process your request at this time.</Text>
            </Container>
            <Container padding="8 0 0 0">
              <Link
                appearance="primary-button"
                href="/"
                newTab={false}
                onClick={() => onStartAgainClick('Restart application')}
              >
                Restart application
              </Link>
            </Container>
          </LayoutContent>
        </Container>
      </LayoutPage>
    );
  }

  const hasOktaProfileBlankMobileNumberError = storeState.applicationErrors?.type === OKTA_EMPTY_MOBILE_NUMBER_ERROR;

  return (
    <LayoutPage
      noProgressStepper={noProgressStepper}
      hideTitleIfErrors={false}
      preHeader={
        showRedMsg && (
          <LayoutContent>
            <Message severity="stop" bg="secondary.pink.t30">
              <Text textStyle="heading6" as="h4" margin="0 0 1 1" color="indigo">
                Code has expired
              </Text>
              <Text textStyle="heading6.bold" margin="0 0 0 1" color="indigo">
                Your {factor === OKTA_SMS_FACTOR ? 'SMS' : 'email'} verification code has expired.{' '}
                <TextButton onClick={onClickRequestNewCode}>Request a new code</TextButton> and try again.
              </Text>
            </Message>
          </LayoutContent>
        )
      }
    >
      <Form
        id={factor === OKTA_SMS_FACTOR ? FORM_ID : EMAIL_VERIFICATION_FORM_ID}
        initialValues={initialValues}
        onSubmit={onSubmit}
        loading={loadingOkta || resuming}
        submitButtonLabel="Verify"
        disableFormButtons={hasOktaProfileBlankMobileNumberError}
        disableFormCache
        hideFormButtons={byCountry({
          AU: true,
          NZ: true,
        })}
      >
        {formData => {
          return (
            <LayoutContent>
              <OneTimeCode
                name="verificationCode"
                label="Verification code"
                testId="verification-code"
                factorTarget={
                  factor === OKTA_SMS_FACTOR
                    ? storeState.maskedMobileNumber
                    : storeState.acquisition.contactDetails.emailAddress
                }
                codeLength="6"
                resendMsecs={RESEND_MIN * 60000}
                onResendCodeClick={onResendCodeClick}
                onResendCodeError={onResendCodeError}
                onTimerExpired={() => setShowRedMsg(true)}
                onTimerReset={() => {
                  setShowRedMsg(false);
                  setResending(false);
                }}
                factor={factor}
                formData={formData}
                theme={theme}
                resending={resending}
              />
              <Nudge title={factor === OKTA_EMAIL_FACTOR && 'Why verify your email?'} />
              {isAU() || isNZ() ? <VerifyButton loading={loadingOkta} /> : null}
              {hasOktaProfileBlankMobileNumberError && <OktaProfileError />}
              <GuideCodeNotReceived contactType={factor === OKTA_SMS_FACTOR ? 'sms' : 'email'} />
            </LayoutContent>
          );
        }}
      </Form>
    </LayoutPage>
  );
}

const VerifyButton = ({ loading }) => (
  <Grid colsGap="10" cols="2">
    <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
      <Grid>
        <Button testId="verify-btn" type="submit" loading={loading}>
          <IconAndText>
            <SecureIcon color="indigo" />
            Verify
          </IconAndText>
        </Button>
      </Grid>
    </Grid.Item>
  </Grid>
);

const IconAndText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  & svg {
    margin-right: 8px;
    margin-top: -2px;
  }
`;
